import React, { useState, useCallback, useMemo } from 'react';
import { Card, CardBody, Input, Button, Chip, Select, SelectItem } from '@heroui/react';
import {
  MagnifyingGlass,
  X,
  CaretLeft,
  CaretRight,
  ArrowRight,
  Funnel,
  Table,
  Link,
} from '@phosphor-icons/react';
import { SearchableRelation, SearchableField } from '../types';
import { RelatedFieldSearch } from '../hooks/useEntityVisualization';

interface EntitySearchPanelProps {
  tableName: string;
  searchableDirectFields: SearchableField[];
  searchableRelations: SearchableRelation[];
  onSearch: (relationPath: string, fieldName: string, searchValue: string) => void;
  onClearSearch: () => void;
  isSearching: boolean;
  activeSearch: RelatedFieldSearch | null;
  searchResultCount: number;
  totalSearchMatches: number;
  currentResultIndex: number;
  onPrevious: () => void;
  onNext: () => void;
}

const EntitySearchPanel: React.FC<EntitySearchPanelProps> = ({
  tableName,
  searchableDirectFields,
  searchableRelations,
  onSearch,
  onClearSearch,
  isSearching,
  activeSearch,
  searchResultCount,
  totalSearchMatches,
  currentResultIndex,
  onPrevious,
  onNext,
}) => {
  const [selectedSource, setSelectedSource] = useState<string>(''); // '_self' for direct, or relationPath
  const [selectedField, setSelectedField] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  // Build combined source options: direct fields + relations
  const sourceOptions = useMemo(() => {
    const options: { key: string; label: string; icon: 'table' | 'link'; fields: SearchableField[] }[] = [];

    // Add direct fields option if available
    if (searchableDirectFields.length > 0) {
      options.push({
        key: '_self',
        label: `${tableName} (huidige entiteit)`,
        icon: 'table',
        fields: searchableDirectFields,
      });
    }

    // Add relation options
    searchableRelations.forEach((relation) => {
      options.push({
        key: relation.relationPath,
        label: `${relation.relationName} (${relation.targetTable})`,
        icon: 'link',
        fields: relation.fields,
      });
    });

    return options;
  }, [tableName, searchableDirectFields, searchableRelations]);

  // Get the fields for the selected source
  const selectedSourceData = sourceOptions.find((s) => s.key === selectedSource);
  const availableFields = selectedSourceData?.fields || [];

  const handleSourceChange = useCallback((value: string) => {
    setSelectedSource(value);
    setSelectedField(''); // Reset field when source changes
  }, []);

  const handleSearch = useCallback(() => {
    if (selectedSource && selectedField && searchValue.trim()) {
      onSearch(selectedSource, selectedField, searchValue.trim());
    }
  }, [selectedSource, selectedField, searchValue, onSearch]);

  const handleClear = useCallback(() => {
    setSelectedSource('');
    setSelectedField('');
    setSearchValue('');
    onClearSearch();
  }, [onClearSearch]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const hasActiveSearch = activeSearch !== null;
  const hasResults = searchResultCount > 0;
  const hasOptions = sourceOptions.length > 0;

  return (
    <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200">
      <CardBody className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Funnel size={20} className="text-indigo-600" />
          <span className="font-semibold text-indigo-800">
            Zoek specifieke record
          </span>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          {/* Source selector (direct fields or relation) */}
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm text-gray-600 mb-1 block">Zoek in</label>
            <Select
              placeholder="Selecteer bron..."
              selectedKeys={selectedSource ? [selectedSource] : []}
              onSelectionChange={(keys) => {
                const value = Array.from(keys)[0] as string;
                handleSourceChange(value || '');
              }}
              variant="bordered"
              size="sm"
              isDisabled={isSearching || !hasOptions}
              startContent={
                selectedSourceData?.icon === 'table' ? (
                  <Table size={16} className="text-indigo-500" />
                ) : selectedSourceData?.icon === 'link' ? (
                  <Link size={16} className="text-purple-500" />
                ) : null
              }
            >
              {sourceOptions.map((option) => (
                <SelectItem
                  key={option.key}
                  startContent={
                    option.icon === 'table' ? (
                      <Table size={16} className="text-indigo-500" />
                    ) : (
                      <Link size={16} className="text-purple-500" />
                    )
                  }
                >
                  {option.label}
                </SelectItem>
              ))}
            </Select>
          </div>

          {/* Field selector */}
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm text-gray-600 mb-1 block">Veld</label>
            <Select
              placeholder="Selecteer veld..."
              selectedKeys={selectedField ? [selectedField] : []}
              onSelectionChange={(keys) => {
                const value = Array.from(keys)[0] as string;
                setSelectedField(value || '');
              }}
              variant="bordered"
              size="sm"
              isDisabled={!selectedSource || isSearching}
            >
              {availableFields.map((field) => (
                <SelectItem key={field.name}>
                  {field.name}
                </SelectItem>
              ))}
            </Select>
          </div>

          {/* Search value input */}
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm text-gray-600 mb-1 block">Zoekwaarde</label>
            <Input
              placeholder="Typ zoekwaarde..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={handleKeyDown}
              variant="bordered"
              size="sm"
              isDisabled={!selectedField || isSearching}
              startContent={<MagnifyingGlass size={16} className="text-gray-400" />}
              endContent={
                searchValue && (
                  <button
                    onClick={() => setSearchValue('')}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={14} />
                  </button>
                )
              }
            />
          </div>

          {/* Search button */}
          <Button
            color="primary"
            size="sm"
            onPress={handleSearch}
            isLoading={isSearching}
            isDisabled={!selectedSource || !selectedField || !searchValue.trim()}
            startContent={!isSearching && <MagnifyingGlass size={16} />}
          >
            Zoeken
          </Button>

          {/* Clear button - only show when there's an active search */}
          {hasActiveSearch && (
            <Button
              color="default"
              variant="flat"
              size="sm"
              onPress={handleClear}
              isDisabled={isSearching}
              startContent={<X size={16} />}
            >
              Wissen
            </Button>
          )}
        </div>

        {/* Active search indicator and navigation */}
        {hasActiveSearch && (
          <div className="mt-3 pt-3 border-t border-indigo-200 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-indigo-700">
              <span className="font-medium">Actieve zoekopdracht:</span>
              <Chip
                size="sm"
                variant="flat"
                color="secondary"
                startContent={
                  activeSearch.relationPath === '_self' ? (
                    <Table size={12} />
                  ) : (
                    <Link size={12} />
                  )
                }
              >
                {activeSearch.relationPath === '_self' ? tableName : activeSearch.relationPath}
              </Chip>
              <ArrowRight size={14} />
              <Chip size="sm" variant="flat" color="secondary">
                {activeSearch.fieldName}
              </Chip>
              <span>=</span>
              <Chip size="sm" variant="flat" color="primary">
                "{activeSearch.searchValue}"
              </Chip>
            </div>

            {hasResults && (
              <div className="flex items-center gap-2 ml-auto">
                <Chip size="sm" variant="flat" color="success">
                  {totalSearchMatches} totaal gevonden
                </Chip>

                {searchResultCount > 1 && (
                  <>
                    <Chip size="sm" variant="flat" color="primary">
                      {currentResultIndex + 1} van {searchResultCount}
                    </Chip>

                    <div className="flex gap-1">
                      <Button
                        isIconOnly
                        size="sm"
                        variant="flat"
                        color="primary"
                        onPress={onPrevious}
                      >
                        <CaretLeft size={16} weight="bold" />
                      </Button>

                      <Button
                        isIconOnly
                        size="sm"
                        variant="flat"
                        color="primary"
                        onPress={onNext}
                      >
                        <CaretRight size={16} weight="bold" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {!hasResults && !isSearching && (
              <Chip size="sm" variant="flat" color="warning" className="ml-auto">
                Geen resultaten gevonden
              </Chip>
            )}
          </div>
        )}

        {/* Helper text when no searchable options available */}
        {!hasOptions && (
          <p className="text-sm text-gray-500 mt-2 italic">
            Geen doorzoekbare velden beschikbaar voor deze entiteit.
          </p>
        )}
      </CardBody>
    </Card>
  );
};

export default EntitySearchPanel;
